<template>
  <div class="relative">
    <swiper
      :modules="modules"
      :slides-per-view="1"
      :space-between="15"
      :autoplay="attrs.autoplay"
      :navigation="attrs.navigation"
      :pagination="{ clickable: true }"
      :centered-slides="false"
      class="carousel"
      @init="onSwiperInit"
    >
      <swiper-slide v-for="(image, index) in attrs.images" :key="image.src" class="carousel-slide">
        <figure class="cursor-pointer" @click="openLightbox(index)">
          <img :src="image.src" :alt="image.alt" :title="image.title" />
          <figcaption v-if="attrs.captions && image.caption">
            {{ image.caption }}
          </figcaption>
        </figure>
      </swiper-slide>
    </swiper>
  </div>

  <VueEasyLightbox
    :visible="lightboxVisible"
    :imgs="lightboxImages"
    :index="lightboxIndex"
    @hide="closeLightbox"
  />
</template>

<script setup lang="ts">
import type { NodeProps } from './RenderNode.vue';
import type { RichImage } from '../extensions/shared/Image.schema';
import { computed, ref } from 'vue';
import { getCarouselAttrs } from '../extensions/Carousel/CarouselExtension';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Autoplay, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

const modules = [Navigation, Autoplay, Pagination];

const props = defineProps<NodeProps>();
const attrs = computed(() => getCarouselAttrs(props.node.attrs));

// Refs
const lightboxVisible = ref(false);
const lightboxIndex = ref(0);

// Computed
const lightboxImages = computed(() =>
  attrs.value.images.map((image: RichImage) => ({
    src: image.src,
    title: image.caption || image.title || image.alt || '',
  })),
);

// Methods
const openLightbox = (index: number) => {
  lightboxIndex.value = index;
  lightboxVisible.value = true;
};

const closeLightbox = () => {
  lightboxVisible.value = false;
};

const onSwiperInit = (swiper: SwiperType) => {
  swiper.params.loop = attrs.value.loop;
};
</script>

<style scoped lang="postcss">
.carousel {
  @apply h-full w-full;
  aspect-ratio: v-bind('attrs.aspectRatio');

  img {
    aspect-ratio: v-bind('attrs.aspectRatio');
  }
}

.carousel-slide {
  @apply flex items-center;
}

.carousel-slide figure {
  @apply relative h-full w-full overflow-hidden;
}

.carousel-slide figure img {
  @apply block h-full w-full select-none object-cover;
}

.carousel-slide figure figcaption {
  @apply absolute bottom-0 left-0 m-0 w-full bg-black/50 p-2 text-center text-white;
}
</style>
